import { useEffect, useState } from "react";
import { ResponsiveContainer, ComposedChart, Area, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, ReferenceLine } from "recharts";
import { ChevronLeft, ChevronRight, ChevronDown, Loader2 } from "lucide-react";
import { addDays, addMonths, addYears, format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { COL, kwh, n } from "./clienteApi";

const iso = (x) => format(x, "yyyy-MM-dd");
const SHIFT = { day: addDays, month: addMonths, year: addYears };
const LABEL = { day: "d MMM yyyy", month: "MMMM yyyy", year: "yyyy" };
const ax = { tick: { fontSize: 10, fill: "#94A3B8" }, axisLine: false, tickLine: false };
const tip = { contentStyle: { borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 } };
const SERIE = [["sole", "Solare", "pv"], ["batt", "Batteria", "batt"], ["casa", "Casa", "load"], ["rete", "Rete", "grid"]];
export const Card = ({ title, children, testid }) => (
  <section className="rounded-3xl bg-white p-4 shadow-sm ring-1 ring-slate-100 sm:p-5" data-testid={testid}>
    {title && <h3 className="mb-3 text-base font-extrabold text-slate-800">{title}</h3>}{children}
  </section>
);

function Nav({ type, setType, date, setDate }) {
  const cur = parseISO(date), today = new Date(), nx = SHIFT[type](cur, 1);
  const can = type === "day" ? iso(nx) <= iso(today) : type === "month" ? format(nx, "yyyy-MM") <= format(today, "yyyy-MM") : nx.getFullYear() <= today.getFullYear();
  const b = "rounded-xl bg-sky-50 p-2 text-sky-600 ring-1 ring-sky-100 transition-colors hover:bg-sky-100 disabled:opacity-30";
  return (
    <div className="space-y-3">
      <div className="mx-auto flex w-fit gap-1 rounded-full bg-slate-100 p-1">
        {[["day", "Oggi"], ["month", "Mese"], ["year", "Anno"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setType(k)} data-testid={`cliente-tab-${k}`}
            className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${type === k ? "bg-sky-500 text-white shadow" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <button type="button" className={b} onClick={() => setDate(iso(SHIFT[type](cur, -1)))} data-testid="cliente-prev" aria-label="Precedente"><ChevronLeft className="h-4 w-4" /></button>
        <span className="text-sm font-bold capitalize text-slate-700" data-testid="cliente-date">{format(cur, LABEL[type], { locale: it })}</span>
        <button type="button" className={b} disabled={!can} onClick={() => setDate(iso(nx))} data-testid="cliente-next" aria-label="Successivo"><ChevronRight className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export function DayChart({ pts, cons, h = "h-60", azienda }) {
  const [on, setOn] = useState({ sole: true, batt: true, casa: true, rete: true });
  const data = pts.map((p) => ({ t: p.t, pv: (p.pv_w || 0) / 1000, batt: p.batt_w == null ? null : p.batt_w / 1000, load: p.load_w == null ? null : p.load_w / 1000, grid: p.grid_w == null ? null : p.grid_w / 1000 }));
  const serie = SERIE.filter(([k]) => cons || k === "sole" || (k === "batt" && data.some((p) => p.batt != null)));
  return (
    <>
      <div className="mb-2 flex flex-wrap justify-center gap-2">
        {serie.map(([k, l0]) => { const l = k === "casa" && azienda ? "Azienda" : l0; return (
          <button key={k} type="button" onClick={() => setOn((o) => ({ ...o, [k]: !o[k] }))} data-testid={`cliente-serie-${k}`}
            className="inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1 text-xs font-bold transition-opacity" style={{ borderColor: COL[k], color: "#334155", opacity: on[k] ? 1 : 0.4 }}>
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: COL[k] }} />{l}</button>
        ); })}
      </div>
      <div className={h}><ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data}>
          <CartesianGrid stroke="#F1F5F9" vertical={false} />
          <XAxis dataKey="t" {...ax} interval="preserveStartEnd" minTickGap={40} />
          <YAxis {...ax} width={30} />
          <Tooltip {...tip} formatter={(v, nm) => [`${n(v, 2)} kW`, nm]} />
          <ReferenceLine y={0} stroke="#CBD5E1" />
          {serie.map(([k, l0, key]) => { const l = k === "casa" && azienda ? "Azienda" : l0; return on[k] && (k === "sole" ? <Area key={k} type="monotone" dataKey={key} name={l} stroke={COL[k]} fill={COL[k]} fillOpacity={0.25} strokeWidth={2} dot={false} />
            : <Line key={k} type="monotone" dataKey={key} name={l} stroke={COL[k]} strokeWidth={1.6} dot={false} />); })}
        </ComposedChart>
      </ResponsiveContainer></div>
      {serie.length > 1 && <p className="mt-1 text-center text-[11px] text-slate-400">kW · batteria e rete sotto lo zero = carica / immissione</p>}
    </>
  );
}

const PeriodChart = ({ pts, cons }) => (
  <div className="h-60"><ResponsiveContainer width="100%" height="100%">
    <BarChart data={pts}>
      <CartesianGrid stroke="#F1F5F9" vertical={false} />
      <XAxis dataKey="t" {...ax} /><YAxis {...ax} width={34} />
      <Tooltip {...tip} formatter={(v, nm) => [`${n(v)} kWh`, nm]} />
      <Bar dataKey="kwh" name="Prodotta" fill={COL.sole} radius={[6, 6, 0, 0]} />
      {cons && <Bar dataKey="consumo" name="Consumata" fill={COL.casa} radius={[6, 6, 0, 0]} />}
    </BarChart>
  </ResponsiveContainer></div>
);

const Tile = ({ l, v, bg, testid }) => (
  <div className="rounded-2xl p-3" style={{ background: bg }} data-testid={testid}>
    <div className="text-xs font-semibold text-slate-600">{l}</div><div className="mt-1 text-xl font-extrabold text-slate-800">{v}</div>
  </div>
);

function Tiles({ r }) {
  const c = r.consumi_disponibili;
  return (
    <div className="grid grid-cols-2 gap-2.5" data-testid="cliente-tiles">
      <Tile l="Energia prodotta" v={kwh(r.prodotta)} bg="#FDE68A" testid="tile-prodotta" />
      <Tile l="Energia consumata" v={c ? kwh(r.consumata) : "in verifica"} bg="#BFDBFE" testid="tile-consumata" />
      {c && <Tile l="Prelevata dalla rete" v={kwh(r.prelevata)} bg="#F1F5F9" testid="tile-prelevata" />}
      {c && <Tile l="Immessa in rete" v={kwh(r.immessa)} bg="#F1F5F9" testid="tile-immessa" />}
      {r.carica != null && <Tile l="Batteria caricata" v={kwh(r.carica)} bg="#D9F99D" testid="tile-carica" />}
      {r.scarica != null && <Tile l="Batteria scaricata" v={kwh(r.scarica)} bg="#BEF264" testid="tile-scarica" />}
    </div>
  );
}

function Donut({ r }) {
  const p = r.provenienza;
  if (!r.consumi_disponibili || !p) return <p className="text-sm text-slate-500" data-testid="cliente-donut-nd">La provenienza dei consumi sarà disponibile appena verificata dal tuo installatore.</p>;
  const rows = [["Da solare", p.fv_diretto, COL.sole], ["Da batteria", p.batteria, COL.batt], ["Dalla rete", p.rete, "#475569"]].filter((x) => x[1] != null);
  const tot = rows.reduce((s, x) => s + Math.max(0, x[1]), 0) || 1;
  return (
    <div data-testid="cliente-donut">
      <div className="relative mx-auto h-48 w-48">
        <ResponsiveContainer><PieChart><Pie data={rows.map(([name, value]) => ({ name, value: Math.max(0, value) }))} dataKey="value" innerRadius={62} outerRadius={84} paddingAngle={3} stroke="none">
          {rows.map((x) => <Cell key={x[0]} fill={x[2]} />)}</Pie></PieChart></ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center"><b className="text-2xl text-slate-800">{n(r.consumata, 2)}</b><span className="text-xs text-slate-500">kWh</span></div>
      </div>
      <div className="mt-3 grid grid-cols-3 text-center">
        {rows.map(([l, v, c]) => <div key={l}><b className="block text-lg" style={{ color: c }}>{n(v, 2)}</b><span className="text-xs text-slate-500">{l}<br />({n((Math.max(0, v) / tot) * 100, 0)}%)</span></div>)}
      </div>
      <p className="mt-4 text-center text-[11px] leading-snug text-slate-400" data-testid="cliente-donut-disclaimer">Dati indicativi: non vanno considerati ai fini fiscali o legali.</p>
    </div>
  );
}

const SocChart = ({ pts }) => (
  <div className="h-44" data-testid="cliente-soc"><ResponsiveContainer width="100%" height="100%">
    <ComposedChart data={pts}><CartesianGrid stroke="#F1F5F9" vertical={false} />
      <XAxis dataKey="t" {...ax} interval="preserveStartEnd" minTickGap={40} /><YAxis {...ax} width={30} domain={[0, 100]} />
      <Tooltip {...tip} formatter={(v) => [`${v}%`, "Carica"]} />
      <Area type="stepAfter" dataKey="soc" stroke={COL.batt} fill={COL.batt} fillOpacity={0.15} strokeWidth={2} dot={false} connectNulls />
    </ComposedChart>
  </ResponsiveContainer></div>
);

const invChip = (active) => `inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold transition-colors ${active ? "border-sky-500 bg-sky-500 text-white" : "border-slate-200 text-slate-500 hover:text-slate-800"}`;
const INV_COL = { ok: "#10B981", avviso: "#F59E0B", guasto: "#EF4444" };

export function ClienteCharts({ src, haBatteria, tick = 0, azienda, inverters, selInit, pro }) {
  const [type, setType] = useState("day");
  const [date, setDate] = useState(iso(new Date()));
  const [d, setD] = useState(null);
  const [sel, setSel] = useState(selInit || []);
  const [openInv, setOpenInv] = useState(!!(selInit && selInit.length));
  const [invKwh, setInvKwh] = useState({});
  useEffect(() => { if (selInit) { setSel(selInit); if (selInit.length) setOpenInv(true); } }, [selInit?.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let live = true;
    setD(null);
    src.line(type, date).then((x) => live && setD(x)).catch(() => live && setD({ points: [], riepilogo: {} }));
    return () => { live = false; };
  }, [type, date]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (tick && date === iso(new Date())) src.line(type, date).then(setD).catch(() => {}); }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pro || !(inverters?.length >= 2)) return;
    let live = true;
    src.line(type, date, inverters.map((v) => v.sn).join(",")).then((x) => {
      if (!live) return;
      const m = {}; (x.inverters || []).forEach((it) => { m[it.sn] = it.kwh; });
      setInvKwh(m);
    }).catch(() => {});
    return () => { live = false; };
  }, [type, date]); // eslint-disable-line react-hooks/exhaustive-deps
  const flipInv = (snv) => {
    if (!sel.length) { setSel([snv]); return; }
    const nx = sel.includes(snv) ? sel.filter((x) => x !== snv) : [...sel, snv];
    setSel(nx.length === inverters.length ? [] : nx);
  };
  const r = d?.riepilogo || {};
  const empty = d && !d.points.some((p) => p.pv_w || p.load_w || p.grid_w || p.kwh || p.consumo || p.soc);
  const invVals = (inverters || []).map((v) => invKwh[v.sn]).filter((x) => x != null && x > 0);
  const invAvg = invVals.length ? invVals.reduce((a, b) => a + b, 0) / invVals.length : null;
  const invOrdered = inverters ? [...inverters].sort((a, b) => (invKwh[a.sn] ?? Infinity) - (invKwh[b.sn] ?? Infinity)) : [];
  const invData = (inverters || []).map((v) => invKwh[v.sn]).filter((x) => x != null);
  const invBest = invData.length ? Math.max(...invData) : null;
  const invWorst = invData.length ? Math.min(...invData) : null;
  return (
    <div className="space-y-4">
      <Card title="Panoramica" testid="cliente-panoramica">
        <Nav type={type} setType={setType} date={date} setDate={setDate} />
        <div className="mt-4">
          {!d ? <div className="flex h-60 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>
            : empty ? <p className="flex h-40 items-center justify-center text-center text-sm text-slate-500" data-testid="cliente-chart-empty">Nessun dato disponibile per questo periodo.</p>
              : type === "day" ? <DayChart pts={d.points} cons={r.consumi_disponibili} azienda={azienda} /> : <PeriodChart pts={d.points} cons={r.consumi_disponibili} />}
        </div>
        {d && <div className="mt-4"><Tiles r={r} /></div>}
        {r.anteprima_non_pubblicata && <p className="mt-3 text-[11px] text-amber-600" data-testid="cliente-anteprima-consumi">Anteprima staff: i consumi non sono ancora pubblicati, il cliente non li vede.</p>}
      </Card>
      {pro && inverters?.length >= 2 && (
        <Card testid="cliente-inv-picker">
          <button type="button" onClick={() => setOpenInv((o) => !o)} className="flex w-full items-center justify-between gap-2" data-testid="cliente-inv-picker-toggle">
            <span className="text-left">
              <b className="block text-base font-extrabold text-slate-800">Confronta inverter</b>
              <span className="text-sm text-slate-500">Dal meno al più produttivo · in ambra chi è sotto la media del periodo</span>
            </span>
            <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${openInv ? "rotate-180" : ""}`} />
          </button>
          {openInv && <>
            {invData.length > 0 && <p className="mb-2 mt-3 text-xs text-slate-500" data-testid="cliente-inv-sommario">Miglior inverter <b className="text-slate-700">{n(invBest, 0)} kWh</b> · peggiore <b className="text-amber-600">{n(invWorst, 0)} kWh</b> · media <b className="text-slate-700">{n(invAvg, 0)} kWh</b></p>}
            <div className="flex flex-wrap gap-1.5" data-testid="cliente-inv-chips">
            <button type="button" onClick={() => setSel([])} className={invChip(!sel.length)} data-testid="cliente-inv-all">Tutti</button>
            {invOrdered.map((v) => { const kv = invKwh[v.sn]; const under = invAvg != null && kv != null && kv < invAvg; return (
              <button key={v.sn} type="button" onClick={() => flipInv(v.sn)} className={`${invChip(sel.length && sel.includes(v.sn))}${under ? " ring-2 ring-amber-400" : ""}`} data-testid={`cliente-inv-chip-${v.sn}`}>
                <span className="inline-block h-2 w-2 rounded-full" style={{ background: INV_COL[v.stato] || INV_COL.ok }} />
                ·{v.sn.slice(-5)}
                {kv != null && <span className={`font-semibold ${under ? "text-amber-600" : "opacity-70"}`}>{n(kv, 0)} kWh</span>}
              </button>
            ); })}
            </div>
          </>}
        </Card>
      )}
      <Card title="Autoconsumo" testid="cliente-autoconsumo">{d ? <Donut r={r} /> : <Loader2 className="h-5 w-5 animate-spin text-sky-500" />}</Card>
      {haBatteria && type === "day" && d && <Card title="Carica della batteria" testid="cliente-batteria-card"><SocChart pts={d.points} /></Card>}
    </div>
  );
}
